import type { FC, PropsWithChildren } from 'react'
import { useTheme } from '@emotion/react'

import Media from '@segment/matcha/components/Media'
import SelectBase from '@segment/matcha/components/Select'
import useSelect from '@segment/matcha/components/Select/useSelect'
import Input from '@segment/matcha/components/Input'
import { ChevronDownIcon } from '@segment/matcha/components/Icons/Common'
import type { Option } from '@segment/matcha/components/Menu/useMenu'

import TableOfContentsBase, * as TableOfContents from '@segment/matcha/recipes/TableOfContents'
import { useTableOfContents } from '@segment/matcha/recipes/TableOfContents'

import type { ListItemProps } from '@segment/matcha/recipes/TableOfContents/ListItem.views'

import { scrollToAnchor } from '@segment/utils/useScrollToAnchor'

interface SidebarProps {
  id: string
  data: Option[]
}

const Sidebar: FC<PropsWithChildren<SidebarProps>> = ({ id, data }) => {
  return (
    <>
      <Media greaterThanOrEqual="xl">
        <Toc data={data} />
      </Media>
      <Media lessThan="xl">
        <MobileSelect id={id} data={data} />
      </Media>
    </>
  )
}

interface TocProps {
  data: SidebarProps[`data`]
}
const Toc: FC<PropsWithChildren<TocProps>> = ({ data }) => {
  const { activeElementId } = useTableOfContents({ ids: data.map((el) => el.value) })

  return (
    <TableOfContentsBase>
      <TableOfContents.List>
        {data.map((item) => (
          <TableOfContentsItem
            key={item.value}
            slug={item.value}
            text={item.text}
            active={activeElementId === item.value}
          />
        ))}
      </TableOfContents.List>
    </TableOfContentsBase>
  )
}

type MobileSelectProps = {
  id: SidebarProps[`id`]
  data: SidebarProps[`data`]
}

const MobileSelect: FC<PropsWithChildren<MobileSelectProps>> = ({ id, data }) => {
  const theme = useTheme()

  const { state, getControlProps, filteredOptions, getSelectProps } = useSelect({
    options: data,
    id,
    onSelect(e) {
      scrollToAnchor(e[0]?.value, -100)
    },
    config: {
      mobileConfirmationBehaviour: false,
    },
    label: `Select a section`,
  })

  return (
    <>
      <Input
        name={id}
        rightSecondIcon={<ChevronDownIcon color={theme.palette.brand.darkAcai[700]} />}
        {...getControlProps({
          placeholder: `Go to section`,
        })}
      />
      <SelectBase
        id={`select-base-${id}`}
        name={`select-base-${id}`}
        options={data}
        filteredOptions={filteredOptions}
        displayMode="mobile"
        open={state.open}
        modalHeadline="Go to section"
        {...getSelectProps()}
      />
    </>
  )
}

type TableOfContentsItemProps = {
  slug: string
  text: string
  active: ListItemProps[`active`]
}

const TableOfContentsItem: FC<PropsWithChildren<TableOfContentsItemProps>> = ({ slug, text, active }) => {
  return (
    <TableOfContents.ListItem
      borderDirection="left"
      href={`#${slug}`}
      onClick={(e) => {
        e.preventDefault()
        scrollToAnchor(slug, -100)
      }}
      active={active}
    >
      <TableOfContents.LeftContent>
        <TableOfContents.Text variant="pMedium">{text}</TableOfContents.Text>
      </TableOfContents.LeftContent>
    </TableOfContents.ListItem>
  )
}

export default Sidebar
